import { useState } from "react";
import { X } from "lucide-react";
import { useChatTheme } from "@/hooks/useChatTheme";
import {
  CHAT_ACCENT_IDS,
  CHAT_ACCENT_PRESETS,
  type ChatAccentId,
} from "@/lib/theme/chatAccent";
import {
  CHAT_NOTIFICATION_SOUND_OPTIONS,
  getChatNotificationSoundOption,
  persistChatNotificationSoundId,
  readChatNotificationSoundId,
  type ChatNotificationSoundId,
} from "@/lib/chatNotificationSoundPrefs";
import { previewChatNotificationSound } from "@/lib/chatNotificationSound";
import { CHAT_SEND_POP_LABEL } from "@/lib/chatSendPopPrefs";

type Props = {
  onClose?: () => void;
};

export function ChatThemePanel({ onClose }: Props) {
  const { theme, accent, setTheme, setAccent } = useChatTheme();
  const isDark = theme === "dark";
  const [soundId, setSoundId] = useState<ChatNotificationSoundId>(() =>
    readChatNotificationSoundId()
  );

  return (
    <div className="chat-theme-panel overflow-y-auto bg-[var(--chat-surface)]">
      <div className="chat-theme-panel__bar">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--chat-muted)]">
          Apparence & son
        </p>
        {onClose ? (
          <button
            type="button"
            className="inline-flex size-8 items-center justify-center rounded-lg text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)] hover:text-[var(--chat-text)]"
            onClick={onClose}
            aria-label="Fermer"
            title="Fermer"
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>

      <div className="px-3 pb-3">
        <p className="mb-1.5 text-[11px] font-medium">Thème</p>
        <div className="mb-3 grid grid-cols-2 gap-1 rounded-lg bg-[var(--chat-bg)] p-0.5">
          <button
            type="button"
            className={`rounded-md px-2 py-1.5 text-xs font-medium transition ${
              !isDark
                ? "bg-[var(--chat-surface)] text-[var(--chat-text)] shadow-sm"
                : "text-[var(--chat-muted)] hover:text-[var(--chat-text)]"
            }`}
            onClick={() => setTheme("light")}
          >
            Clair
          </button>
          <button
            type="button"
            className={`rounded-md px-2 py-1.5 text-xs font-medium transition ${
              isDark
                ? "bg-[var(--chat-surface-elevated)] text-[var(--chat-text)] shadow-sm"
                : "text-[var(--chat-muted)] hover:text-[var(--chat-text)]"
            }`}
            onClick={() => setTheme("dark")}
          >
            Sombre
          </button>
        </div>

        <p className="mb-2 text-[11px] font-medium">Couleur d&apos;accent</p>
        <div className="mb-4 grid grid-cols-6 gap-1.5" role="listbox" aria-label="Couleur d'accent">
          {CHAT_ACCENT_IDS.map((id) => {
            const preset = CHAT_ACCENT_PRESETS[id];
            const selected = accent === id;
            return (
              <button
                key={id}
                type="button"
                role="option"
                aria-selected={selected}
                title={preset.label}
                className={`flex aspect-square items-center justify-center rounded-lg border transition ${
                  selected
                    ? "border-[var(--chat-accent)] ring-2 ring-[var(--chat-accent)]/35"
                    : "border-[var(--chat-border)] hover:bg-[var(--chat-surface-elevated)]"
                }`}
                onClick={() => setAccent(id as ChatAccentId)}
              >
                <span
                  className="size-6 shrink-0 rounded-full border border-black/10"
                  style={{ backgroundColor: preset.swatch }}
                  aria-hidden
                />
              </button>
            );
          })}
        </div>

        <div className="mb-3 rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2">
          <p className="text-[11px] font-medium text-[var(--chat-text)]">Son d&apos;envoi</p>
          <p className="text-[10px] text-[var(--chat-muted)]">{CHAT_SEND_POP_LABEL}</p>
        </div>

        <p className="mb-1.5 text-[11px] font-medium">
          Son de notification · {getChatNotificationSoundOption(soundId).label}
        </p>
        <div className="overflow-hidden rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)]">
          {CHAT_NOTIFICATION_SOUND_OPTIONS.map((opt, i) => {
            const selected = soundId === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-xs transition ${
                  i > 0 ? "border-t border-[var(--chat-border)]" : ""
                } ${
                  selected
                    ? "bg-[var(--chat-accent-soft)] font-semibold text-[var(--chat-accent-strong)]"
                    : "text-[var(--chat-text)] hover:bg-[var(--chat-surface-elevated)]"
                }`}
                onClick={() => {
                  persistChatNotificationSoundId(opt.id);
                  setSoundId(opt.id);
                  void previewChatNotificationSound(opt.id);
                }}
              >
                <span>{opt.label}</span>
                {selected ? <span aria-hidden>✓</span> : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
